import "./landing.css";

/**
 * The host's front door: pick a project, or paste a URL, and open it in the
 * viewer or the studio.
 *
 * This page is host chrome, not product. The viewer and the studio are the
 * products (@dev/viewer and @dev/studio); this only builds links to them, so it
 * pulls in neither and stays a few kB.
 */

/**
 * This page used to BE the viewer, so links of the form /?config=<url> exist in
 * the wild. Hand them to the viewer before anything renders, so an old link
 * still lands on its model instead of flashing a chooser it did not ask for.
 */
const params = new URLSearchParams(window.location.search);
if (params.has("config") || params.has("glb")) {
    window.location.replace(`./viewer.html${window.location.search}`);
}

/** Where the build (or the dev server) publishes the list of config documents. */
const CONFIG_MANIFEST = "./config/index.json";

interface IProjectCard {
    url: string;
    title: string;
    subtitle: string;
    readable: boolean;
}

/** The configs this deployment advertises. Fetched, not compiled in, so it describes the server. */
async function listConfigs(): Promise<string[]> {
    try {
        const response = await fetch(CONFIG_MANIFEST, { cache: "no-store" });
        if (!response.ok) return [];
        const files = (await response.json()) as unknown;
        return Array.isArray(files) ? files.filter((file): file is string => typeof file === "string").map((file) => `./config/${file}`) : [];
    } catch {
        return [];
    }
}

/**
 * Read a config's own meta, so the card names the project rather than the file.
 *
 * Returns null when the document is not there. That is NOT an error worth showing:
 * a manifest travels with the build, while the documents are deployed per site, so
 * a project deliberately left off this server would otherwise be advertised as a
 * broken link, which both misleads the visitor and names a project this
 * deployment was meant not to mention. A document that IS served but unreadable
 * is a different story, and does get a card.
 */
async function describe(url: string): Promise<IProjectCard | null> {
    let response: Response;
    try {
        response = await fetch(url);
    } catch {
        return null;
    }
    if (!response.ok) return null;
    try {
        const config = (await response.json()) as { meta?: { projectName?: string; companyName?: string } };
        const title = config.meta?.projectName ?? url;
        const company = config.meta?.companyName;
        return { url, title, subtitle: company !== undefined ? `${company}, ${url}` : url, readable: true };
    } catch (error) {
        return { url, title: url, subtitle: `unreadable: ${error instanceof Error ? error.message : String(error)}`, readable: false };
    }
}

function viewerHref(config: string): string {
    return `./viewer.html?config=${encodeURIComponent(config)}`;
}

function studioHref(query: Record<string, string>): string {
    const search = new URLSearchParams(query);
    return `./studio.html?${search.toString()}`;
}

function renderCard(card: IProjectCard): HTMLElement {
    const element = document.createElement("div");
    element.className = "ln-card";
    element.innerHTML = `
        <div class="ln-card-text">
            <div class="ln-card-title"></div>
            <div class="ln-card-sub"></div>
        </div>
        <a class="ln-btn ln-btn-ghost" data-el="studio">Studio</a>
        <a class="ln-btn" data-el="viewer">Viewer</a>`;

    const title = element.querySelector<HTMLElement>(".ln-card-title");
    const sub = element.querySelector<HTMLElement>(".ln-card-sub");
    const viewer = element.querySelector<HTMLAnchorElement>('[data-el="viewer"]');
    const studio = element.querySelector<HTMLAnchorElement>('[data-el="studio"]');

    // textContent, not innerHTML: this comes from a fetched document.
    if (title !== null) title.textContent = card.title;
    if (sub !== null) {
        sub.textContent = card.subtitle;
        sub.dataset.error = String(!card.readable);
    }
    if (viewer !== null) viewer.href = viewerHref(card.url);
    // A local config opens in the studio as a SEED: its texts and ui blocks are
    // kept while every coordinate is re-measured from the GLB it points at.
    if (studio !== null) studio.href = studioHref({ config: card.url });
    return element;
}

/**
 * Resolve every advertised config, then render the ones that answered.
 *
 * All at once rather than card-by-card as each settles: appending on resolution
 * would order the list by whichever config replied first, reshuffling the page
 * between reloads, and a card that then had to disappear would flash.
 */
async function fillProjects(root: HTMLElement): Promise<void> {
    const section = root.querySelector<HTMLElement>('[data-el="projects"]');
    const cards = root.querySelector<HTMLElement>('[data-el="cards"]');
    if (cards === null) return;

    const described = await Promise.all((await listConfigs()).map(describe));
    const present = described.filter((card): card is IProjectCard => card !== null);
    for (const card of present) cards.appendChild(renderCard(card));

    // Revealed only once populated. It starts hidden so a deployment that serves no
    // project never flashes an empty section, which reads as broken rather than as
    // a site that simply bundles none.
    if (present.length > 0) section?.removeAttribute("hidden");
}

function build(root: HTMLElement): void {
    root.innerHTML = `
        <div class="ln-wrap">
            <div class="ln-brand">
                <img src="img/brand/geomodels-logo-72.png" alt="" />
                <h1>GEOMODELS</h1>
            </div>
            <p class="ln-tagline">3D geological model viewer, and the studio that authors its configuration.</p>

            <section class="ln-section" data-el="projects" hidden>
                <h2>Projects on this server</h2>
                <p>Served from <code>config/</code>, listed by <code>config/index.json</code>.</p>
                <div class="ln-cards" data-el="cards"></div>
            </section>

            <section class="ln-section">
                <h2>Open a config by URL</h2>
                <p>Any project configuration document. A cross-origin URL needs permissive CORS.</p>
                <div class="ln-row">
                    <input data-el="config" type="url" placeholder="https://host/project/config.json" />
                    <button class="ln-btn ln-btn-ghost" data-el="config-studio" type="button" disabled>Studio</button>
                    <button class="ln-btn" data-el="config-viewer" type="button" disabled>Viewer</button>
                </div>
                <p class="ln-note">The studio treats a config as a seed: it keeps the titles, texts and ui blocks, and re-measures every coordinate from the GLB.</p>
            </section>

            <section class="ln-section">
                <h2>Author a GLB from scratch</h2>
                <p>Studio only. Leave it empty to drag and drop a local file instead.</p>
                <div class="ln-row">
                    <input data-el="glb" type="url" placeholder="https://host/project/model.glb" />
                    <button class="ln-btn" data-el="glb-studio" type="button">Studio</button>
                </div>
            </section>
        </div>`;

    void fillProjects(root);

    const configInput = root.querySelector<HTMLInputElement>('[data-el="config"]');
    const configViewer = root.querySelector<HTMLButtonElement>('[data-el="config-viewer"]');
    const configStudio = root.querySelector<HTMLButtonElement>('[data-el="config-studio"]');
    const glbInput = root.querySelector<HTMLInputElement>('[data-el="glb"]');
    const glbStudio = root.querySelector<HTMLButtonElement>('[data-el="glb-studio"]');

    const configValue = (): string => configInput?.value.trim() ?? "";
    const syncConfig = (): void => {
        const empty = configValue() === "";
        if (configViewer !== null) configViewer.disabled = empty;
        if (configStudio !== null) configStudio.disabled = empty;
    };
    configInput?.addEventListener("input", syncConfig);
    configInput?.addEventListener("keydown", (event) => {
        if (event.key === "Enter" && configValue() !== "") window.location.assign(viewerHref(configValue()));
    });
    configViewer?.addEventListener("click", () => window.location.assign(viewerHref(configValue())));
    configStudio?.addEventListener("click", () => window.location.assign(studioHref({ config: configValue() })));

    glbStudio?.addEventListener("click", () => {
        const glb = glbInput?.value.trim() ?? "";
        // Empty is valid: the studio then shows its drop card.
        window.location.assign(glb === "" ? "./studio.html" : studioHref({ glb }));
    });
    syncConfig();
}

const root = document.getElementById("landing");
if (root !== null) build(root);
